import React, { useEffect, useState } from "react";

import { useQuery } from "@apollo/client";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { formatISO } from "date-fns";
import { useNavigate, useParams } from "react-router-dom";

import { DescriptiveBanner } from "@/components/Banner/DescriptiveBanner";
import { GreetingBanner } from "@/components/Banner/GreetingBanner";
import { UrgentBanner } from "@/components/Banner/UrgentBanner";
import { ContainedButton } from "@/components/Button";
import { VerticalScrollContainer } from "@/components/Container/VerticalScrollContainer";
import PersonAddIcon from "@/components/Icons/PersonAddIcon";
import { Spacer } from "@/components/Spacer/Spacer";
import { Tabs } from "@/components/Tab/Tabs";
import { SubscriptionStatus } from "@/gql/graphql";
import { useVisibilityState } from "@/hooks/useVisibilityState";
import { colors } from "@/theme/overrides/colors";

import { FoodJournalingTab } from "./components/FoodJournalingTab";
import { ModuleAnsweringTab } from "./components/ModuleAnsweringTab";
import SidePanel from "./components/SidePanel";
import SuspendedDialog from "./components/SuspendedDialog";
import { WeightLoggingTab } from "./components/WeightLoggingTab";
import { coachViewerQuery } from "./graphql/dashboardPageQueries";
import { CriticalBanner } from "../../components/Banner/CriticalBanner";
import NewClientDialog from "../Clients/ClientList/components/NewClientDialog";

const clientCategoryTabs = [
  {
    value: "foodJournaling",
    label: "Food Journaling",
  },
  {
    value: "weightLogging",
    label: "Weight Logging",
  },
  {
    value: "modules",
    label: "Modules",
  },
];

const DashboardPage: React.FC = () => {
  const { tab } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const currentDate = formatISO(new Date(), { representation: "date" });
  const [selectedClientCategoryTabValue, setSelectedClientCategoryTabs] =
    useState(clientCategoryTabs[0].value);
  const trialEndBanner = useVisibilityState();
  const suspendedBanner = useVisibilityState();
  const suspendedDialog = useVisibilityState();
  const createClientDialog = useVisibilityState();

  // Queries
  const { data: coachData, loading: isLoadingCoach } = useQuery(
    coachViewerQuery,
    {
      variables: {
        currentDate: currentDate,
      },
    }
  );

  const coach = coachData?.coachViewer;

  useEffect(() => {
    if (coach?.trialWillEnd) {
      trialEndBanner.show();
    }
  }, [coach?.trialWillEnd]);

  useEffect(() => {
    if (coach?.subscriptionSuspended) {
      suspendedDialog.show();
    }
  }, [coach?.subscriptionSuspended]);

  const renderTabContent = () => {
    switch (tab) {
      case "modules":
        return <ModuleAnsweringTab />;
      case "weightLogging":
        return <WeightLoggingTab />;
      case "foodJournaling":
      default:
        return <FoodJournalingTab />;
    }
  };

  // Functions
  const handleDismissSuspendedDialogIntoBanner = () => {
    suspendedDialog.hide();
    suspendedBanner.show();
  };

  const topContent = (
    <>
      {trialEndBanner.isVisible && (
        <>
          <DescriptiveBanner
            title="Your free trial period is about to expire."
            buttonText="Manage subscription"
            onClose={trialEndBanner.hide}
            onClick={() => navigate("/subscription")}
          />
          <Spacer h={24} />
        </>
      )}
      {coach?.subscriptionStatus == SubscriptionStatus.PastDue && (
        <>
          <UrgentBanner
            title="Payment Failed"
            message="Something went wrong and we couldn't process your payment. As we care for you and your client, we are not suspending your account until our retry period is finished. If you continue to have issues, kindly try another payment method."
            buttonText="Manage subscription"
            onClose={trialEndBanner.hide}
            onClick={() => navigate("/subscription")}
          />
          <Spacer h={24} />
          <Spacer h={24} />
        </>
      )}
      {suspendedBanner.isVisible && (
        <>
          <CriticalBanner
            title="Payment Failed"
            message="Please settle your account subscription as soon as possible to continue your full access to NutriCoach application."
            buttonText="Manage subscription"
            onClick={() => navigate("/subscription")}
            onClose={suspendedBanner.hide}
          />
          <Spacer h={24} />
        </>
      )}
      <GreetingBanner fullName={coach?.profile?.fullName ?? "..."} />
      <Spacer h={24} />
    </>
  );

  return (
    <>
      {/* On mobile the greeting/banners stay pinned to the very top, above the
          summary panel which is reordered above the client list. */}
      {isMobile && <Box p={3}>{topContent}</Box>}
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        height={{ xs: "auto", md: "100%" }}
      >
        <VerticalScrollContainer
          p={{ xs: 3, md: 6 }}
          pt={{ xs: 0, md: 6 }}
          width={{ xs: "100%", md: "75%" }}
        >
          {!isMobile && topContent}
          <Box
            bgcolor="white"
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            gap={2}
            p={4}
          >
            <Tabs
              tabs={clientCategoryTabs}
              selectedTabValue={selectedClientCategoryTabValue}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              onChange={(_, value) => {
                setSelectedClientCategoryTabs(value);
                navigate(`/${value}`);
              }}
            />
            <ContainedButton
              text="Add New Client"
              startIcon={<PersonAddIcon />}
              onClick={createClientDialog.show}
            />
          </Box>
          <Spacer h={12} />
          {renderTabContent()}
        </VerticalScrollContainer>
        <VerticalScrollContainer
          bgcolor={colors.theme.system.white}
          width={{ xs: "100%", md: "25%" }}
          order={{ xs: -1, md: 0 }}
        >
          <SidePanel loading={isLoadingCoach} />
        </VerticalScrollContainer>
      </Box>
      <NewClientDialog
        open={createClientDialog.isVisible}
        onClose={createClientDialog.hide}
        afterCreate={async () => {
          createClientDialog.hide();
        }}
      />
      <SuspendedDialog
        isVisible={suspendedDialog.isVisible}
        onClose={handleDismissSuspendedDialogIntoBanner}
      />
    </>
  );
};

export default DashboardPage;
